import { useState, useRef } from "react";
import { base44 } from "../../api/base44Client";
import {
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Camera,
  Upload,
  FileText,
  ArrowRight,
  X,
  RefreshCw,
  Image,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

function QualityHints() {
  return (
    <Box
      sx={{
        bgcolor: "#fff9db",
        border: "1px solid #ffe066",
        borderRadius: 3,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "#856404",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        📸 Consigli per una buona foto
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          "& li": { fontSize: "0.75rem", color: "#856404" },
        }}
      >
        <li>• Assicurati che il documento sia ben visibile</li>
        <li>• Evita riflessi e immagini sfocate</li>
        <li>• Fotografa il documento per intero</li>
        <li>• Usa buona illuminazione</li>
      </Box>
    </Box>
  );
}

function ImagePreview({ label, url, onRemove }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <Box
        component="img"
        src={url}
        alt={label}
        sx={{ width: "100%", height: 128, objectFit: "cover" }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          px: 1,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "white",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <CheckCircle2 size={12} color="#4caf50" /> {label}
        </Typography>
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ color: "white", "&:hover": { color: "#ffcdd2" } }}
        >
          <X size={14} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function DocumentScanner({ onExtracted, onSkip }) {
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontUrl, setFrontUrl] = useState(null);
  const [backUrl, setBackUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const dropRef = useRef(null);

  async function uploadFile(file) {
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    return file_url;
  }

  async function handleFiles(files, side) {
    const file = files[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const url = await uploadFile(file);
      if (side === "front") {
        setFrontFile(file);
        setFrontUrl(url);
        setFrontPreview(URL.createObjectURL(file));
      } else {
        setBackFile(file);
        setBackUrl(url);
        setBackPreview(URL.createObjectURL(file));
      }
    } catch (err) {
      setError("Errore nel caricamento del file.");
    }
    setUploading(false);
  }

  async function handleFileInput(e, side) {
    await handleFiles(e.target.files, side);
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf",
    );
    if (files.length > 0) handleFiles([files[0]], "front");
    if (files.length > 1) handleFiles([files[1]], "back");
  }

  async function handleAnalyze() {
    if (!frontUrl) return;
    setAnalyzing(true);
    setError(null);
    try {
      const res = await base44.functions.invoke("extractDocumentData", {
        file_front_url: frontUrl,
        file_back_url: backUrl || undefined,
      });
      const data = res.data;
      if (!data.success || data.fallback) {
        setError(
          data.error || "Estrazione fallita. Puoi compilare manualmente.",
        );
      } else {
        onExtracted({
          extracted: data.extracted,
          formFields: data.form_fields,
          frontUrl,
          backUrl,
          extractedAt: data.extracted_at,
        });
      }
    } catch (err) {
      setError("Errore durante l'analisi.");
    }
    setAnalyzing(false);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 4,
            bgcolor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
          }}
        >
          <Image size={28} color="white" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Scansiona il tuo documento
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 300, mx: "auto" }}
        >
          Carica o fotografa il documento di identità. Compileremo
          automaticamente i dati principali.
        </Typography>
      </Box>

      <Box
        ref={dropRef}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed",
          borderRadius: 4,
          p: 3,
          textAlign: "center",
          transition: "all 0.2s",
          borderColor: dragOver ? "primary.main" : "divider",
          bgcolor: dragOver ? "primary.light" : "action.hover",
          opacity: dragOver ? 0.8 : 1,
        }}
      >
        {!frontPreview ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Upload size={32} color="gray" />
            <Typography variant="caption" color="text.secondary">
              Trascina qui il documento oppure
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Camera size={18} />}
                onClick={() => frontInputRef.current?.click()}
                disabled={uploading}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Fronte documento
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileText size={18} />}
                onClick={() => backInputRef.current?.click()}
                disabled={uploading || !frontPreview}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Retro documento
              </Button>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.625rem" }}
            >
              JPG, PNG, WEBP, PDF
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ImagePreview
                  label="Fronte"
                  url={frontPreview}
                  onRemove={() => {
                    setFrontFile(null);
                    setFrontUrl(null);
                    setFrontPreview(null);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                {backPreview ? (
                  <ImagePreview
                    label="Retro"
                    url={backPreview}
                    onRemove={() => {
                      setBackFile(null);
                      setBackUrl(null);
                      setBackPreview(null);
                    }}
                  />
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => backInputRef.current?.click()}
                    disabled={uploading}
                    sx={{
                      height: 128,
                      borderRadius: 3,
                      borderStyle: "dashed",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      textTransform: "none",
                    }}
                  >
                    <Camera size={20} />
                    <Typography variant="caption">
                      + Retro (opzionale)
                    </Typography>
                  </Button>
                )}
              </Grid>
            </Grid>
            {uploading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={14} color="inherit" />
                <Typography variant="caption" color="text.secondary">
                  Caricamento in corso...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <input
        ref={frontInputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => handleFileInput(e, "front")}
      />
      <input
        ref={backInputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => handleFileInput(e, "back")}
      />

      <QualityHints />

      <Alert severity="info" variant="outlined" sx={{ borderRadius: 3 }}>
        <Typography variant="caption" sx={{ color: "info.main" }}>
          🔒 Il documento viene elaborato esclusivamente per estrarre i dati
          necessari alla richiesta di finanziamento. Potrai verificare e
          correggere tutte le informazioni prima dell'invio.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>
          <AlertTitle sx={{ fontWeight: 700, fontSize: "0.75rem", mb: 0 }}>
            Lettura non riuscita
          </AlertTitle>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleAnalyze}
          disabled={!frontUrl || uploading || analyzing}
          startIcon={
            analyzing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RefreshCw size={20} />
            )
          }
          sx={{ textTransform: "none", py: 1.5, borderRadius: 2.5 }}
        >
          {analyzing ? "Analisi in corso con AI..." : "Analizza documento"}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={onSkip}
          endIcon={<ArrowRight size={18} />}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          Continua manualmente senza scansione
        </Button>
      </Box>
    </Box>
  );
}
